import { some, trimEnd, replace } from 'lodash'
import { getNominative } from '../../../common/utils'

/**
 * Formats the output for a question, based on the answer, comment, question,
 * and other custom properties for the field.
 *
 * @param {String} answer the answer to the field's question
 * @param {String} comment a comment added by the user in addition to the answer
 * @param {Object} field contains all properties for the current field
 * @param {Object} resident the current resident
 */
export default function formatFieldOutput({
  answer,
  comment,
  field,
  resident,
  preferredPeopleNominative
}) {
  let outputAnswer = answer
  let outputQuestion = field.hasOwnProperty('outputQuestion')
    ? field.outputQuestion
    : field.label
  let outputComment = comment

  if (outputAnswer instanceof Array) {
    outputAnswer = formatArrayAnswer(outputAnswer)
  } else {
    outputAnswer = addFullStop(outputAnswer)
  }

  if (outputComment) {
    outputAnswer += ` ${addFullStop(outputComment)}`
  }

  outputQuestion = formatWithResidentInfo(
    outputQuestion,
    resident,
    preferredPeopleNominative
  )
  outputAnswer = formatWithResidentInfo(
    outputAnswer,
    resident,
    preferredPeopleNominative
  )

  if (field.outputQuestionHidden) {
    return `${outputAnswer}\n\n`
  } else if (field.hasOwnProperty('outputQuestion')) {
    return `${outputQuestion} ${outputAnswer}\n\n`
  } else {
    return `${outputQuestion}: ${outputAnswer}\n\n`
  }
}

/**
 * Formats answers in the case that there are multiple answers to one question,
 * i.e. checkbox fields. Need to insert commas etc. in the right places, and
 * concatenate answers in a readable way.
 *
 * @param {Array} answers all answers for the current question
 */
function formatArrayAnswer(answers) {
  const hasLongAnswer = some(answers, (answer) => answer.length >= 50)
  if (answers.length > 2) {
    const delimiter = hasLongAnswer ? '; ' : ', '
    return answers
      .map((answer, i) => {
        const trimmed = trimFullStop(answer)
        const isLastAnswer = i === answers.length - 1
        if (hasLongAnswer) {
          return isLastAnswer ? addFullStop(trimmed) : trimmed
        } else {
          return isLastAnswer ? `and ${trimmed}.` : trimmed
        }
      })
      .join(delimiter)
  } else if (answers.length === 2) {
    const delimiter = hasLongAnswer ? '; ' : ' and '
    return `${trimFullStop(answers[0])}${delimiter}${addFullStop(answers[1])}`
  } else {
    return addFullStop(answers[0])
  }
}

function trimFullStop(str) {
  const trimmed = trimEnd(str)
  return trimmed.match(/\.$/) ? trimmed.slice(0, -1) : trimmed
}

function addFullStop(str) {
  const trimmed = trimEnd(str)
  return trimmed.match(/\.$/) ? trimmed : `${trimmed}.`
}

/**
 * Replaces string placeholders such as with their corresponding values, e.g.
 * $first_name$ gets replaced by a resident's first name. These placeholders
 * are inserted during the form building process by the user.
 *
 * @param {String} str
 * @param {Object} resident
 */
export function formatWithResidentInfo(
  str,
  resident,
  preferredPeopleNominative
) {
  let output = str
  getReplacePatterns(resident, preferredPeopleNominative).forEach(
    ({ oldStr, newStr }) => {
      output = replace(output, oldStr, newStr)
    }
  )

  return output
}

const getReplacePatterns = (resident, preferredPeopleNominative) => {
  const isMale = resident.gender === 'male'
  return [
    { oldStr: /\$first_name\$/g, newStr: resident.name.first },
    {
      oldStr: /\$name\$/g,
      newStr: `${resident.name.first} ${resident.name.last}`
    },
    { oldStr: /\$he_she_they\$/g, newStr: isMale ? 'he' : 'she' },
    { oldStr: /\$he\$/g, newStr: isMale ? 'he' : 'she' },
    { oldStr: /\$He_She_They\$/g, newStr: isMale ? 'He' : 'She' },
    { oldStr: /\$He\$/g, newStr: isMale ? 'He' : 'She' },
    { oldStr: /\$his_her_their\$/g, newStr: isMale ? 'his' : 'her' },
    { oldStr: /\$his\$/g, newStr: isMale ? 'his' : 'her' },
    { oldStr: /\$him\$/g, newStr: isMale ? 'him' : 'her' },
    { oldStr: /\$him_her_them\$/g, newStr: isMale ? 'him' : 'her' },
    { oldStr: /\$His_Her_Their\$/g, newStr: isMale ? 'His' : 'Her' },
    { oldStr: /\$His\$/g, newStr: isMale ? 'His' : 'Her' },
    {
      oldStr: /\$preferred_people_nominative_singular\$/g,
      newStr: getNominative(
        preferredPeopleNominative,
        'Individual'
      )?.toLowerCase()
    },
    {
      oldStr: /\$preferred_people_nominative_plural\$/g,
      newStr: getNominative(
        preferredPeopleNominative,
        'Individuals'
      )?.toLowerCase()
    }
  ]
}
